"use client";

import { useState, useEffect } from "react";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { toast } from "sonner";
import { Sparkles, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useTranslations } from "next-intl";
import { cn } from "@/lib/utils";

export default function Ratings({
  productName = null,
  rating = 0,
  ratingsNumber = 0,
  productId,
  hasBtn = true,
}) {
  const t = useTranslations("rating");
  const [newRating, setNewRating] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [fingerprint, setFingerprint] = useState(null);
  const [hasRated, setHasRated] = useState(false);

  useEffect(() => {
    const loadFingerprint = async () => {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      setFingerprint(result.visitorId);
    };

    loadFingerprint();
  }, []);

  const roundedRating = Math.round(rating * 2) / 2;
  const filledStars = Math.floor(roundedRating);
  const hasHalfStar = roundedRating % 1 !== 0;

  const handleStarClick = (index) => {
    setNewRating(index + 1);
  };

  const handleSubmit = async () => {
    try {
      const response = await fetch("/api/rate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          productId,
          rating: newRating,
          fingerprint,
        }),
      });

      const result = await response.json();
      if (!result.success && result.already_rated)
        return toast.error(t("alreadyRated"));

      setHasRated(true);
      toast.success(t("rateSuccess"));
      setIsOpen(false);
      setNewRating(0);
      window.location.reload();
    } catch (error) {
      console.error("Error submitting rating:", error);
    }
  };

  return (
    <div
      className={cn("flex flex-wrap items-center", hasBtn && " justify-center")}
    >
      <div className="flex items-center space-x-2 whitespace-nowrap">
        <div className="flex rtl:flex-row-reverse" aria-hidden="true">
          {[...Array(5)].map((_, index) => {
            const isFilledStar = index < filledStars;
            const isHalfStar = index === filledStars && hasHalfStar;

            return (
              <div key={index} className="relative h-5 w-5">
                <Star className="absolute h-5 w-5 stroke-current text-gray-300" />

                <Star
                  className={cn("absolute h-5 w-5 fill-current", {
                    "visible text-yellow-400": isFilledStar || isHalfStar,
                    invisible: !isFilledStar && !isHalfStar,
                  })}
                  style={
                    isHalfStar
                      ? { clipPath: "inset(0 50% 0 0)", position: "relative" }
                      : {}
                  }
                />
              </div>
            );
          })}
        </div>

        <span
          className="text-sm font-medium text-gray-700"
          aria-label={`${rating} out of 5 stars`}
        >
          ({rating.toFixed(1)} {t("stars")})
        </span>
        <span> • </span>
        <span className="text-sm text-gray-500">
          {ratingsNumber} {t("reviews")}
        </span>
      </div>
      {hasBtn && (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild>
            <Button
              variant="link"
              size="sm"
              className="m-auto gap-2.5 text-secondary"
            >
              <Sparkles className="size-5 " /> {t("rateProduct")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>
                {t("rate")} {productName}
              </DialogTitle>
            </DialogHeader>
            <div className="flex justify-center space-x-2 py-4">
              {[...Array(5)].map((_, index) => (
                <Star
                  key={index}
                  className={`h-8 w-8 cursor-pointer ${
                    index < newRating
                      ? "fill-current text-yellow-400"
                      : "text-gray-300"
                  }`}
                  onClick={() => handleStarClick(index)}
                />
              ))}
            </div>
            <Button onClick={handleSubmit} disabled={newRating === 0}>
              {t("send")}
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
